// Typed client for UI-only endpoints at /api/ui/* (Plan 2-BACKEND.md §4, not part of the Keitaro contract).
import { http } from './http'
import type { UserAccessData } from './types'

const BASE = '/api/ui'

export interface MeResponse {
  id: number
  login: string
  type: 'ADMIN' | 'USER'
  timezone: string
  /** Keitaro's access_data object; the legacy bootstrap ADMIN stores `[]` (fixture 042). */
  access_data: UserAccessData | unknown[]
  /** The session's CSRF token (`internal/api/ui/auth.go` me): the SPA re-arms `X-CSRF-Token`
   * from it after a reload. Absent for API-key principals. */
  csrfToken?: string
  mustChangePassword?: boolean
  totpEnabled?: boolean
}

export const authApi = {
  login: (login: string, password: string) =>
    http.post<
      | {
          ok: true
          requiresTotp?: boolean
          /** Set alongside `requiresTotp` when the account has no TOTP secret yet and policy
           * demands one (ADMIN + `Require2FAForAdmin`) — the pending login only unlocks
           * `/auth/2fa/enrol{,/confirm}`, not `/auth/2fa/verify` (`internal/auth/service.go`
           * `Login`'s `EnrolTOTP`). */
          enrolTotp?: boolean
          csrfToken?: string
          mustChangePassword?: boolean
        }
      | { ok: false; error: string }
    >(`${BASE}/auth/login`, { login, password }, { skipAuthRedirect: true }),
  verifyTotp: (login: string, code: string) =>
    http.post<
      { ok: true; csrfToken: string; mustChangePassword?: boolean } | { ok: false; error: string }
    >(`${BASE}/auth/2fa/verify`, { login, code }, { skipAuthRedirect: true }),
  /** First step of first-login enrolment: issues a fresh secret + `otpauth://` URL for the pending
   * login. `{ok:false,error:"Login again"}` once the 5-minute pending window has expired. */
  enrolBegin: (login: string) =>
    http.post<{ ok: true; secret: string; otpauth: string } | { ok: false; error: string }>(
      `${BASE}/auth/2fa/enrol`,
      { login },
      { skipAuthRedirect: true },
    ),
  /** Confirms the code against the secret from `enrolBegin`; on success the backend stores the
   * secret and opens the session exactly like a normal login. */
  enrolConfirm: (login: string, secret: string, code: string) =>
    http.post<
      { ok: true; csrfToken: string; mustChangePassword?: boolean } | { ok: false; error: string }
    >(`${BASE}/auth/2fa/enrol/confirm`, { login, secret, code }, { skipAuthRedirect: true }),
  logout: () => http.post<void>(`${BASE}/auth/logout`),
  me: () => http.get<MeResponse>(`${BASE}/me`, undefined, { skipAuthRedirect: true }),
}

export interface UiSettings {
  general: { productName: string; timezone: string; currency: string }
  tracking: {
    uniquenessMethod: 'ip' | 'ip_ua'
    uniquenessTtlHours: number
    cookieTtlHours: number
    offerTokenTtlHours: number
    funnelBasePath: string
    protectFunnelFolders: boolean
  }
  /** Single tracker-wide postback key used in `/{postback_key}/postback` (Plan 2 B-D8) — not
   * per-campaign; rotated on Settings → Postback. */
  postback_key: string
  postback: {
    allowedStatuses: string[]
    revenueParam: string
  }
  notFoundHtml: string
  bots: { builtinList: boolean }
  geoip: { provider: string; licenseKey: string; lastUpdate: string }
  cleaner: { rawRetentionMonths: number; keepAggregates: boolean }
  backups: { schedule: string; destination: string }
  alerts: {
    telegramBotToken: string
    telegramChatId: string
    smtp: string
    backlogThreshold: number
    errorRateSpikePct: number
  }
  security: {
    adminIpAllowlist: string
    idleSessionMinutes: number
    absoluteSessionHours: number
    enforce2fa: boolean
  }
  aegiesShield: { trustedCidrs: string; hmacSecretSet: boolean }
  outboundPostbacksEnabled: boolean
}

export const settingsApi = {
  get: () => http.get<UiSettings>(`${BASE}/settings`),
  update: (body: Partial<UiSettings>) => http.put<UiSettings>(`${BASE}/settings`, body),
  rotatePostbackKey: () => http.post<{ postback_key: string }>(`${BASE}/settings/postback/rotate`),
}

export interface HealthService {
  name: string
  status: 'ok' | 'warn' | 'err'
  version: string
  uptime: string
  last: string
  note?: string
}

export interface HealthResponse {
  services: HealthService[]
  metrics: Record<string, number[]>
  canary: { ago: string; ok: boolean; detail: string }
  errors: Array<{
    time: string
    service: string
    level: string
    message: string
    count: number
    first: string
    last: string
    /** Structured part of the latest occurrence (err, folder, dependency, status …). */
    detail?: Record<string, string>
  }>
}

/** One span of the Health page's Incidents panel (`internal/api/ui/incidents.go`): a probed
 * service down → recovered, a run of canary failures, or a silence of the health collector
 * itself (the ingest service / ClickHouse was down, so nothing could be recorded). */
export interface Incident {
  what: string
  kind: 'dependency' | 'canary' | 'collector'
  start: string
  end?: string
  duration: string
  detail: string
  ongoing: boolean
}

export const healthApi = {
  get: () => http.get<HealthResponse>(`${BASE}/health`),
  runCanary: () => http.post<{ ok: boolean; detail: string }>(`${BASE}/canary/run`),
  incidents: (days = 7) =>
    http.get<{ days: number; incidents: Incident[] }>(`${BASE}/health/incidents`, {
      days: String(days),
    }),
}

export interface LogRow {
  ts: string
  type: string
  service: string
  level: string
  message: string
  /** The record's structured attributes (tds_logs `fields`): what "php-fpm failed" was about. */
  fields?: Record<string, string>
}

export const logsApi = {
  list: (type: string, params?: { q?: string; level?: string; limit?: number }) =>
    http.get<LogRow[]>(`${BASE}/logs/${type}`, params),
}

/** Per-account state Keitaro's user entity does not carry (GET /api/ui/users/status). */
export interface AccountStatus {
  id: number
  login: string
  createdAt: string
  lastLoginAt: string
  failedAttempts: number
  lockedUntil: string
  locked: boolean
  totpEnabled: boolean
  mustChangePassword: boolean
}

export const usersUiApi = {
  generateOneTimePassword: (id: number | string) =>
    http.post<{ password: string; mustChange: boolean }>(`${BASE}/users/${id}/one-time-password`),
  resetSessions: (id: number | string) =>
    http.post<{ ok: true }>(`${BASE}/users/${id}/sessions/reset`),
  /** Releases the "Too many attempts. Try again in 15 minutes." lockout. */
  unlock: (id: number | string) => http.post<{ ok: true }>(`${BASE}/users/${id}/unlock`),
  status: () => http.get<AccountStatus[]>(`${BASE}/users/status`),
}

export interface DashboardSummary {
  /** clicks (every logged click, 404s included), uc_campaign, conversions, cost,
   * revenue_confirmed, pl_confirmed, roi_confirmed, plus not_found / not_found_unique (clicks the
   * campaign logged but answered with nothing — no flow, gate 404) and reached / reached_unique. */
  kpis: Record<string, number>
  hourly: Array<Record<string, number | string>>
  /** Keitaro `destination` split: "LP" | "Offer" | "Campaign" | "" (nothing shown). */
  destinations?: Array<{ destination: string; clicks: number; uc: number }>
}

export const dashboardApi = {
  summary: (params?: Record<string, string>) =>
    http.get<DashboardSummary>(`${BASE}/dashboard/summary`, params),
}

/** One line of the Funnel page (`internal/api/ui/funnel.go` funnelRow): where a campaign's,
 * landing page's or offer's clicks ended up. `dead` = nothing shown (404 / no flow / gate),
 * `lp_only` = landing page shown and never clicked through, `offer` = offer reached (directly or
 * via /go), `handed` = passed to another campaign. Rates are percentages or null without a base. */
export interface FunnelRow {
  id: number
  name: string
  state?: string
  clicks: number
  uc: number
  bots: number
  dead: number
  lp_only: number
  offer: number
  handed: number
  lp_views: number
  lp_clicks: number
  lp_ctr: number | null
  leads: number
  sales: number
  cr: number | null
  dead_rate: number | null
}
export interface FunnelError {
  folder: string
  entity: string
  kind: 'landing' | 'offer' | ''
  count: number
  last: string
  what: string
}
export interface FunnelResponse {
  totals: FunnelRow
  campaigns: FunnelRow[]
  landings: FunnelRow[]
  offers: FunnelRow[]
  errors: FunnelError[]
}
export const funnelApi = {
  get: (params?: Record<string, string>) => http.get<FunnelResponse>(`${BASE}/funnel`, params),
}

export interface TrafficSimulateRequest {
  ip: string
  userAgent: string
  country?: string
  campaignId?: number
  params?: Record<string, string>
}

export interface TrafficSimulateResult {
  trace: Array<{ step: string; ok: boolean; detail: string }>
  destination: string
}

export const simulateApi = {
  run: (body: TrafficSimulateRequest) =>
    http.post<TrafficSimulateResult>(`${BASE}/traffic-simulate`, body),
}

export const exportApi = {
  request: (body: { kind: string; format: 'csv' | 'xlsx'; ids?: Array<number | string> }) =>
    http.post<{ jobId: string }>(`${BASE}/export`, body),
}

/** One row of the Game page leaderboard (owner request; UI-only, not a Keitaro entity). */
export interface GameLeaderboardRow {
  login: string
  best_score: number
  updated_at: string
}

export const gameApi = {
  leaderboard: () => http.get<GameLeaderboardRow[]>(`${BASE}/game/leaderboard`),
  submitScore: (score: number) => http.post<GameLeaderboardRow>(`${BASE}/game/score`, { score }),
}
